import { CONFIG } from '../config';
import { clamp } from '../core/math';
import { Random } from '../core/Random';
import type { ItemId, Stats } from './types';

export const DAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

/** 0 = Monday ... 6 = Sunday. Day 1 of the game is a Monday. */
export function dayOfWeek(day: number): number {
  return (((day - 1) % 7) + 7) % 7;
}

export function dayName(day: number): string {
  return DAY_NAMES[dayOfWeek(day)] ?? 'Someday';
}

export function weekOf(day: number): number {
  return Math.floor((day - 1) / 7) + 1;
}

export function rentDueDay(week: number): number {
  return (week - 1) * 7 + 1 + CONFIG.rentDayOfWeek;
}

/** Lot rent climbs every week once the new owners take over. */
export function rentForWeek(week: number): number {
  return CONFIG.rentBase + CONFIG.rentHike * Math.max(0, week - 1);
}

export function absoluteMinutes(day: number, minutes: number): number {
  return (day - 1) * 1440 + minutes;
}

export function hourOf(minutes: number): number {
  return Math.floor(minutes / 60) % 24;
}

export function formatClock(minutes: number): string {
  const total = ((Math.floor(minutes) % 1440) + 1440) % 1440;
  const h24 = Math.floor(total / 60);
  const mm = total % 60;
  const suffix = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm.toString().padStart(2, '0')} ${suffix}`;
}

export function isNight(minutes: number): boolean {
  const h = minutes / 60;
  return h >= 20.5 || h < 6;
}

/** Can you go to bed for the night (vs. just napping)? */
export function isBedtime(minutes: number): boolean {
  const h = hourOf(minutes);
  return h >= CONFIG.bedtimeHour || h < 6;
}

export function minutesUntilWake(minutes: number): number {
  return minutes < CONFIG.wakeMinutes ? CONFIG.wakeMinutes - minutes : 1440 - minutes + CONFIG.wakeMinutes;
}

export function isPassOutTime(minutes: number): boolean {
  return minutes >= CONFIG.passOutMinutes && minutes < CONFIG.wakeMinutes;
}

export interface Rank {
  title: string;
  min: number;
  blurb: string;
  /** Her version, where it's different. */
  her?: { title?: string; blurb?: string };
}

export const RANKS: readonly Rank[] = [
  { title: 'Fresh Off the Lease', min: 0, blurb: 'You still think the HOA is coming.' },
  { title: 'Porch Sitter', min: 15, blurb: 'The couch outside knows your shape.' },
  { title: 'Certified Redneck', min: 35, blurb: 'Your truck nutz have truck nutz.' },
  { title: 'Cinder Block Baron', min: 60, blurb: 'Your car has more blocks than miles.' },
  { title: 'Double-Wide Dreamer', min: 90, blurb: 'You can taste the extra square footage.' },
  {
    title: 'Trailer Park Royalty',
    min: 130,
    blurb: 'Darlene says "sir" now. Sometimes.',
    her: { blurb: 'Darlene says "ma\'am" now. Sometimes.' },
  },
  {
    title: 'Lord of Lucky Acres',
    min: 180,
    blurb: 'Tornadoes ask permission.',
    her: { title: 'Lady of Lucky Acres' },
  },
];

/** Your rank for a Redneck Rating; `woman` picks her titles (Lady of Lucky Acres). */
export function rankFor(
  rating: number,
  woman = false,
): { rank: Rank; index: number; next: Rank | undefined } {
  let index = 0;
  for (let i = 0; i < RANKS.length; i++) {
    if (rating >= (RANKS[i] as Rank).min) index = i;
  }
  const say = (r: Rank | undefined): Rank | undefined => (r && woman && r.her ? { ...r, ...r.her } : r);
  return { rank: say(RANKS[index]) as Rank, index, next: say(RANKS[index + 1]) };
}

export type BuzzTier = 'sober' | 'good' | 'buzzed' | 'hammered';

export function buzzTier(buzz: number): BuzzTier {
  if (buzz >= 85) return 'hammered';
  if (buzz >= 60) return 'buzzed';
  if (buzz >= 25) return 'good';
  return 'sober';
}

export const BUZZ_LABELS: Record<BuzzTier, string> = {
  sober: 'Stone Cold Sober',
  good: "Feelin' Good",
  buzzed: 'Buzzed',
  hammered: 'Hammered',
};

/** Liquid courage: positive clout is boosted while you've got a good buzz on. */
export function cloutMultiplier(buzz: number): number {
  const tier = buzzTier(buzz);
  return tier === 'good' || tier === 'buzzed' ? 1.5 : 1;
}

export interface DecayOptions {
  asleep?: boolean;
  heatWave?: boolean;
}

/** Advance needs by a number of game minutes. Pure: returns a new Stats object. */
export function decayStats(stats: Stats, minutes: number, opts: DecayOptions = {}): Stats {
  const h = minutes / 60;
  const next: Stats = { ...stats };
  if (opts.asleep) {
    next.hunger -= 2 * h;
    next.energy += 12 * h;
    next.buzz -= 14 * h;
  } else {
    next.hunger -= 5 * h;
    next.energy -= (opts.heatWave ? 7 : 5) * h;
    next.buzz -= 8 * h;
  }
  if (stats.hunger <= 0) next.health -= 6 * h;
  else if (stats.buzz >= 90) next.health -= 3 * h;
  else if (stats.hunger > 40 && stats.energy > 20) next.health += (opts.asleep ? 5 : 3) * h;

  next.health = clamp(next.health, 0, 100);
  next.hunger = clamp(next.hunger, 0, 100);
  next.energy = clamp(next.energy, 0, 100);
  next.buzz = clamp(next.buzz, 0, 100);
  return next;
}

// ---------------------------------------------------------------------------
// Scratch-off tickets
// ---------------------------------------------------------------------------

export const SCRATCH_SYMBOLS = [':beer:', ':gator:', ':lifted-truck:', ':cash:', ':gem:'] as const;
export type ScratchSymbol = (typeof SCRATCH_SYMBOLS)[number];

export const SCRATCH_PRIZES: Record<ScratchSymbol, number> = {
  ':beer:': 2,
  ':gator:': 5,
  ':lifted-truck:': 20,
  ':cash:': 100,
  ':gem:': 500,
};

/** Odds per ticket. Expected return is ~72%, which is generous by lottery standards. */
export const SCRATCH_ODDS: ReadonlyArray<{ symbol: ScratchSymbol; p: number }> = [
  { symbol: ':beer:', p: 0.15 },
  { symbol: ':gator:', p: 0.06 },
  { symbol: ':lifted-truck:', p: 0.015 },
  { symbol: ':cash:', p: 0.003 },
  { symbol: ':gem:', p: 0.0005 },
];

export interface ScratchGame {
  /** The item that is this ticket. */
  item: ItemId;
  name: string;
  price: number;
  prizes: Record<ScratchSymbol, number>;
  odds: ReadonlyArray<{ symbol: ScratchSymbol; p: number }>;
  /** Ticket colors. */
  bg: string;
  ink: string;
}

const oddsOf = (ps: readonly number[]): ReadonlyArray<{ symbol: ScratchSymbol; p: number }> =>
  SCRATCH_SYMBOLS.map((symbol, i) => ({ symbol, p: ps[i] as number }));

/**
 * Bigger tickets pay a little better, like the real thing. Expected returns run
 * from about 66% (Cash Cow) to 75% (Trailer Park Millionaire).
 */
export const SCRATCH_GAMES: Record<string, ScratchGame> = {
  scratcher: {
    item: 'scratcher',
    name: 'LUCKY 7s',
    price: 2,
    prizes: SCRATCH_PRIZES,
    odds: SCRATCH_ODDS,
    bg: '#fffbe6',
    ink: '#7a1f2b',
  },
  scratchCow: {
    item: 'scratchCow',
    name: 'CASH COW',
    price: 5,
    prizes: { ':beer:': 5, ':gator:': 10, ':lifted-truck:': 50, ':cash:': 250, ':gem:': 2500 },
    odds: oddsOf([0.15, 0.08, 0.015, 0.002, 0.0002]),
    bg: '#eaf7e4',
    ink: '#1f5a22',
  },
  scratchGator: {
    item: 'scratchGator',
    name: 'GATOR GOLD',
    price: 10,
    prizes: { ':beer:': 10, ':gator:': 25, ':lifted-truck:': 100, ':cash:': 1000, ':gem:': 10000 },
    odds: oddsOf([0.16, 0.06, 0.015, 0.0015, 0.0001]),
    bg: '#fff3c4',
    ink: '#7a5a00',
  },
  scratchMega: {
    item: 'scratchMega',
    name: 'TRAILER PARK MILLIONAIRE',
    price: 20,
    prizes: { ':beer:': 20, ':gator:': 50, ':lifted-truck:': 500, ':cash:': 5000, ':gem:': 100000 },
    odds: oddsOf([0.17, 0.07, 0.008, 0.0006, 0.00001]),
    bg: '#f2e6ff',
    ink: '#4b1f7a',
  },
};

export interface ScratchTicket {
  cells: ScratchSymbol[];
  winner: ScratchSymbol | null;
  prize: number;
  game: ScratchGame;
}

export function ticketWinner(cells: readonly ScratchSymbol[]): ScratchSymbol | null {
  const counts = new Map<ScratchSymbol, number>();
  for (const c of cells) counts.set(c, (counts.get(c) ?? 0) + 1);
  for (const [symbol, n] of counts) if (n >= 3) return symbol;
  return null;
}

export function generateScratchTicket(
  rng: Random,
  multiplier = 1,
  game: ScratchGame = SCRATCH_GAMES.scratcher as ScratchGame,
): ScratchTicket {
  let roll = rng.next();
  let winner: ScratchSymbol | null = null;
  for (const { symbol, p } of game.odds) {
    if (roll < p) {
      winner = symbol;
      break;
    }
    roll -= p;
  }

  const cells: ScratchSymbol[] = [];
  const counts = new Map<ScratchSymbol, number>();
  if (winner) {
    for (let i = 0; i < 3; i++) cells.push(winner);
    counts.set(winner, 3);
  }
  while (cells.length < 6) {
    const options = SCRATCH_SYMBOLS.filter((s) => s !== winner && (counts.get(s) ?? 0) < 2);
    const s = rng.pick(options);
    cells.push(s);
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  const shuffled = rng.shuffle(cells);
  return { cells: shuffled, winner, prize: winner ? game.prizes[winner] * multiplier : 0, game };
}

// ---------------------------------------------------------------------------
// Mega Bucks: the weekly lotto
// ---------------------------------------------------------------------------

export const LOTTO = {
  price: 2,
  /** Pick 5 different numbers from 1 to this. */
  max: 30,
  picks: 5,
  /** Drawn every Saturday (0 = Monday) at 8 PM. */
  drawDay: 5,
  drawMinutes: 20 * 60,
  maxTickets: 10,
  /** Prize by numbers matched. */
  prizes: [0, 0, 2, 20, 500, 50000] as readonly number[],
} as const;

/** The same numbers for everybody, every week: anyone you're playing with can check their tickets too. */
export function lottoDraw(week: number): number[] {
  const rng = new Random(0x10770 + week * 7919);
  const pool = Array.from({ length: LOTTO.max }, (_, i) => i + 1);
  return rng
    .shuffle(pool)
    .slice(0, LOTTO.picks)
    .sort((a, b) => a - b);
}

export function lottoMatches(ticket: readonly number[], draw: readonly number[]): number {
  return ticket.filter((n) => draw.includes(n)).length;
}

export function quickPick(rng: Random): number[] {
  const pool = Array.from({ length: LOTTO.max }, (_, i) => i + 1);
  return rng
    .shuffle(pool)
    .slice(0, LOTTO.picks)
    .sort((a, b) => a - b);
}

/** A valid ticket: five different whole numbers in range. */
export function validLottoTicket(nums: readonly number[]): boolean {
  return (
    nums.length === LOTTO.picks &&
    new Set(nums).size === LOTTO.picks &&
    nums.every((n) => Number.isInteger(n) && n >= 1 && n <= LOTTO.max)
  );
}

/** The week a drawing at this time belongs to, if it has happened yet. */
export function lottoWeekDrawn(day: number, minutes: number): number {
  // The drawing for week w happens on day (w - 1) * 7 + drawDay + 1 at drawMinutes.
  const week = weekOf(day);
  const drawDayAbs = (week - 1) * 7 + LOTTO.drawDay + 1;
  if (day > drawDayAbs || (day === drawDayAbs && minutes >= LOTTO.drawMinutes)) return week;
  return week - 1;
}
